import { useCallback, useContext } from 'react';
import { getToken } from '../../auth/api';
import { UserContext } from '../../auth/UserContext';
import { OWNER_ID } from '../../constants/constants';
import type { LanguageCode, TranslationData, TranslationInput, TranslationResult } from './types';
import { TranslationValidationError } from './types';

interface UseTranslationOptions {
    /** Source language. Omit to let the service auto-detect. */
    sourceLanguage?: LanguageCode;

    /** Content format sent to the service. Defaults to `"text"`. */
    format?: TranslationInput['format'];
}

interface UseTranslationReturn {
    /** Trigger a translation for the given text and target languages. Throws on failure. */
    translate: (text: string, targetLanguages: LanguageCode[]) => Promise<TranslationResult[]>;
}

const TRANSLATION_ENDPOINT = '/translation/v1/translate';
const DEFAULT_FORMAT: TranslationInput['format'] = 'text';

export function useTranslation({
    sourceLanguage,
    format = DEFAULT_FORMAT
}: UseTranslationOptions = {}): UseTranslationReturn {
    const { activeOrganizationAccount } = useContext(UserContext);

    const translate = useCallback(
        async (text: string, targetLanguages: LanguageCode[]): Promise<TranslationResult[]> => {
            if (targetLanguages.length === 0 || !text.trim()) {
                throw new TranslationValidationError(
                    'Translation requires at least one target language and non-empty text.'
                );
            }

            const token = getToken();
            const headers = new Headers();
            headers.append('Authorization', token ? 'Bearer ' + token.token : '');
            headers.append('Content-Type', 'application/json');
            headers.append('Accept', 'application/json');

            if (activeOrganizationAccount) {
                headers.append(OWNER_ID, activeOrganizationAccount);
            }

            const body: Record<string, unknown> = {
                targetLanguages,
                text,
                format
            };

            if (sourceLanguage) {
                body.sourceLanguage = sourceLanguage;
            }

            try {
                const response = await fetch(TRANSLATION_ENDPOINT, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(body)
                });

                if (!response.ok) {
                    throw new Error(
                        `Translation request failed with status ${response.status}: ${response.statusText}`
                    );
                }

                const data: TranslationData = await response.json();

                return data.translations;
            } catch (error: unknown) {
                const message = error instanceof Error ? error.message : String(error);
                throw new Error(`Translation request failed: ${message}`);
            }
        },
        [activeOrganizationAccount, format, sourceLanguage]
    );

    const result: UseTranslationReturn = { translate };

    return result;
}
